import { BrandLogo } from '@affine/component';
import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import * as Dialog from '@radix-ui/react-dialog';
import {
  type ReactNode,
  type Ref,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useTransitionState } from 'react-transition-state';

import * as styles from './search-surface.css';
import {
  initialSearchSurfaceState,
  mountedSearchSurfaceModes,
  reduceSearchSurface,
  type SearchSurfaceMode,
} from './search-surface-state';

interface Size {
  width: number;
  height: number;
}

const QUICK_TOP = '13vh';

function frameTop(mode: SearchSurfaceMode, height: number) {
  return mode === 'quick'
    ? QUICK_TOP
    : `max(0px, calc(50dvh - ${height / 2}px))`;
}

// Hidden views shrink and blur away; the incoming view settles out of the
// same state while the frame morphs, so a switch reads as one transformation.
const LAYER_HIDDEN: Keyframe = {
  opacity: 0,
  transform: 'translateX(-50%) scale(0.96)',
  filter: 'blur(6px)',
};
const LAYER_SHOWN: Keyframe = {
  opacity: 1,
  transform: 'translateX(-50%) scale(1)',
  filter: 'blur(0px)',
};

const SurfaceLayer = ({
  children,
  onSize,
  visible,
  inert,
}: {
  children: ReactNode;
  onSize: (size: Size) => void;
  visible: boolean;
  inert: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const onSizeRef = useRef(onSize);
  onSizeRef.current = onSize;
  const wasVisible = useRef<boolean | null>(null);

  // Measure before paint so the frame never renders without a target size.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const report = () =>
      onSizeRef.current({
        width: element.offsetWidth,
        height: element.offsetHeight,
      });
    report();
    const observer = new ResizeObserver(report);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const previous = wasVisible.current;
    wasVisible.current = visible;
    // The first frame takes its state as is; only real switches animate.
    if (previous === null) {
      element.style.opacity = visible ? '1' : '0';
      return;
    }
    if (previous === visible) return;
    element.style.opacity = visible ? '1' : '0';
    streamLainAnimationController.play(
      element,
      visible ? [LAYER_HIDDEN, LAYER_SHOWN] : [LAYER_SHOWN, LAYER_HIDDEN],
      visible ? 'morph' : 'feedback'
    );
  }, [visible]);

  useLayoutEffect(() => {
    const element = ref.current;
    return () => {
      if (element) streamLainAnimationController.cancel(element);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={styles.layer}
      data-visible={visible}
      aria-hidden={visible ? undefined : true}
      inert={inert}
    >
      {children}
    </div>
  );
};

/**
 * One dialog frame shared by quick search and advanced search. A persistent
 * header carries the mode switch; switching views shrinks the old one away
 * while the frame morphs to the new size, then settles the new one in; the
 * animated logo covers any remaining load time.
 */
export const SearchSurface = ({
  mode,
  ready,
  quick,
  advanced,
  header,
  title,
  onClose,
  contentRef,
  workspaceViewId,
  workspaceId,
}: {
  mode: SearchSurfaceMode | null;
  ready: Record<SearchSurfaceMode, boolean>;
  quick: ReactNode;
  advanced: ReactNode;
  /** Stays in place across view switches, e.g. the quick/advanced toggle. */
  header?: ReactNode;
  title: string;
  onClose: () => void;
  contentRef?: Ref<HTMLDivElement>;
  workspaceViewId?: string;
  workspaceId?: string;
}) => {
  const [state, dispatch] = useReducer(
    reduceSearchSurface,
    initialSearchSurfaceState
  );
  const [sizes, setSizes] = useState<Partial<Record<SearchSurfaceMode, Size>>>(
    {}
  );
  const lastSize = useRef<Size | null>(null);
  const readyRef = useRef(ready);
  readyRef.current = ready;
  const [{ status: presence }, togglePresence] = useTransitionState({
    timeout: styles.PRESENCE_MS,
  });

  useEffect(() => {
    togglePresence(mode !== null);
    dispatch({
      type: 'target',
      mode,
      ready: mode ? readyRef.current[mode] : true,
    });
  }, [mode, togglePresence]);

  useEffect(() => {
    if (state.phase !== 'morphing' || !state.active) return;
    const target = state.active;
    const timer = setTimeout(
      () => dispatch({ type: 'morph-end', ready: readyRef.current[target] }),
      styles.MORPH_MS
    );
    return () => clearTimeout(timer);
  }, [state.active, state.phase]);

  const activeReady = state.active ? ready[state.active] : false;
  useEffect(() => {
    if (state.phase === 'waiting' && activeReady) dispatch({ type: 'ready' });
  }, [activeReady, state.phase]);

  const displayMode = state.active ?? state.shown;
  const displaySize = (displayMode && sizes[displayMode]) || lastSize.current;
  lastSize.current = displaySize;

  const views: Record<SearchSurfaceMode, ReactNode> = {
    quick: <div className={styles.quickView}>{quick}</div>,
    advanced,
  };
  const headerHeight = header ? styles.HEADER_HEIGHT : 0;

  return (
    <Dialog.Root
      modal
      open={presence !== 'exited'}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} data-mode={displayMode} />
        <Dialog.Content
          ref={contentRef}
          data-workspace-view-id={workspaceViewId}
          data-workspace-id={workspaceId}
          className={styles.frame}
          data-testid="search-surface"
          data-mode={displayMode}
          data-phase={state.phase}
          data-morphing={state.phase === 'morphing'}
          data-presence={presence}
          data-has-header={!!header}
          aria-describedby={undefined}
          // Each view focuses its own input once mounted.
          onOpenAutoFocus={event => event.preventDefault()}
          style={
            displayMode && displaySize
              ? {
                  width: displaySize.width,
                  height: displaySize.height + headerHeight,
                  top: frameTop(displayMode, displaySize.height + headerHeight),
                }
              : undefined
          }
        >
          <Dialog.Title className={styles.visuallyHidden}>{title}</Dialog.Title>
          {header ? <div className={styles.header}>{header}</div> : null}
          {mountedSearchSurfaceModes(state).map(view => (
            <SurfaceLayer
              key={view}
              visible={state.shown === view}
              inert={view !== state.active && state.active !== null}
              onSize={size =>
                setSizes(current => {
                  const previous = current[view];
                  return previous?.width === size.width &&
                    previous.height === size.height
                    ? current
                    : { ...current, [view]: size };
                })
              }
            >
              {views[view]}
            </SurfaceLayer>
          ))}
          <div
            className={styles.loader}
            aria-hidden="true"
            data-visible={state.phase === 'waiting'}
            data-testid="search-surface-loader"
          >
            <BrandLogo size={48} animate />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
